<template>
  <section class="mt-10">
    <div class="bg-white dark:bg-gray-800 relative shadow-md sm:rounded-lg overflow-hidden">
      <div class="border-b dark:border-gray-700 mx-4">
        <div class="flex items-center justify-between space-x-4 pt-3">
          <div class="flex-1 flex items-center space-x-3">
            <h5 class="dark:text-white font-semibold">
              Pages
            </h5>
          </div>
        </div>
        <div class="flex flex-col-reverse md:flex-row items-center justify-between md:space-x-4 py-3">
          <div class="w-full lg:w-2/3 flex flex-col space-y-3 md:space-y-0 md:flex-row md:items-center">
            <form class="w-full md:max-w-sm flex-1 md:mr-4">
              <label
                class="text-sm font-medium text-gray-900 sr-only dark:text-white"
                for="page-search"
              >Search</label>
              <input
                id="page-search"
                v-model="searchQuery"
                class="block w-full p-2 pl-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                placeholder="Search pages..."
                type="search"
              >
            </form>
          </div>
          <div
            class="w-full md:w-auto flex flex-col md:flex-row mb-3 md:mb-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
          >
            <router-link
              class="flex items-center justify-center text-white bg-black hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-primary-600 dark:hover:bg-primary-700 focus:outline-none dark:focus:ring-primary-800"
              to="/administrator/pages/new"
            >
              <svg
                class="h-3.5 w-3.5 mr-2"
                fill="none"
                height="12"
                viewBox="0 0 12 12"
                width="12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 0.75V11.25M0.75 6H11.25"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </svg>
              Add New Page
            </router-link>
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th
                class="px-4 py-3"
                scope="col"
              >
                Title
              </th>
              <th
                class="px-4 py-3"
                scope="col"
              >
                Slug
              </th>
              <th
                class="px-4 py-3"
                scope="col"
              >
                Status
              </th>
              <th
                class="px-4 py-3"
                scope="col"
              >
                Updated
              </th>
              <th
                class="px-4 py-3"
                scope="col"
              >
                <span class="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody v-if="loading">
            <tr class="border-b dark:border-gray-600">
              <td
                class="px-4 py-4 text-sm text-gray-500"
                colspan="5"
              >
                Loading pages...
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="filteredPages.length === 0">
            <tr class="border-b dark:border-gray-600">
              <td
                class="px-4 py-4 text-sm text-gray-500"
                colspan="5"
              >
                {{ searchQuery ? 'No pages match your search.' : 'No pages yet.' }}
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr
              v-for="page in filteredPages"
              :key="page._id"
              class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <th
                class="px-4 py-3 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                scope="row"
              >
                {{ page.title }}
              </th>
              <td class="px-4 py-3">
                {{ page.slug }}
              </td>
              <td class="px-4 py-3">
                {{ page.status }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                {{ formatDate(page.updatedAt) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <router-link
                    class="flex items-center justify-center w-10 h-10 text-gray-700 bg-white border border-gray-300 rounded-lg md:w-10 focus:outline-none hover:bg-gray-100 hover:text-primary-700 focus:z-10 focus:ring-4 focus:ring-gray-200"
                    :to="`/administrator/pages/${page._id}`"
                    aria-label="Edit page"
                    title="Edit"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20,16v4a2,2,0,0,1-2,2H4a2,2,0,0,1-2-2V6A2,2,0,0,1,4,4H8"
                        fill="none"
                        stroke="#000000"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                      />
                      <polygon
                        fill="none"
                        points="12.5 15.8 22 6.2 17.8 2 8.3 11.5 8 16 12.5 15.8"
                        stroke="#000000"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                      />
                    </svg>
                  </router-link>
                  <button
                    class="flex items-center justify-center w-10 h-10 text-red-500 bg-white border border-red-400 rounded-lg md:w-10 focus:outline-none hover:bg-gray-100 hover:text-red-600 focus:z-10 focus:ring-4 focus:ring-gray-200"
                    type="button"
                    aria-label="Delete page"
                    title="Delete"
                    @click="deletePage(page._id)"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 7H20"
                        stroke="#ff5c5c"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                      />
                      <path
                        d="M6 10L7.70141 19.3578C7.87432 20.3088 8.70258 21 9.66915 21H14.3308C15.2974 21 16.1257 20.3087 16.2986 19.3578L18 10"
                        stroke="#ff5c5c"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                      />
                      <path
                        d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z"
                        stroke="#ff5c5c"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                      />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import axios from 'axios';
import {useHead} from "@vueuse/head";
import {Page} from '@/types';

export default defineComponent({
  name: 'Pages',
  data() {
    useHead({
      title: 'Pages',
    });

    return {
      pages: [] as Page[],
      loading: false,
      searchQuery: '',
    };
  },
  computed: {
    filteredPages(): Page[] {
      const query = this.searchQuery.toLowerCase();
      if (!query) return this.pages;
      return this.pages.filter(page =>
        page.title.toLowerCase().includes(query) || page.slug.toLowerCase().includes(query)
      );
    },
  },
  async created() {
    await this.fetchPages();
  },
  methods: {
    async fetchPages() {
      this.loading = true;
      try {
        const response = await axios.get<Page[]>('/pages');
        this.pages = response.data;
      } catch (error) {
        console.error('Failed to load pages:', error);
      } finally {
        this.loading = false;
      }
    },
    async deletePage(id: string) {
      try {
        await axios.delete(`/pages/${id}`);
        this.pages = this.pages.filter(page => page._id !== id);
      } catch (error) {
        console.error('Failed to delete page:', error);
      }
    },
    formatDate(date?: string) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    },
  },
});
</script>
